import React, {useMemo} from 'react'
import loadable from '@loadable/component'
import {Box, useRecipe} from '@chakra-ui/react3'
import {Global} from '@emotion/react'
import messageBarRecipe from './index.recipe'
import {Chakra3Theme} from '../../theme'
import CMSContent from '../../../components/cms-content'
import useCmsContent, {
    TYPE_CONTENT_SLOT
} from '../../../components/cms-content/hooks/use-cms-content'
import prepareHtmlForInjection from '../../../components/cms-content/util/prepare-html-for-injection'
import {findMessageBar} from '../../modules/message-bar/slides'

// The module registry stays out of the app shell bundle, the message bar renders on every page
const CmsModules = loadable(() => import('../../modules'))

/**
 * Promotional message bar rendered as a static, full-width element at the very
 * top of the page — above the header and in normal document flow, so it scrolls
 * away as the user scrolls down.
 *
 * Renders the `message-bar` CMS content slot and keeps its content full width. The slot API
 * returns the slot's `c_body` either as an array of Leap modules or as the HTML of legacy
 * modules. Of the Leap modules, only the first message bar with slides renders, as the
 * header height chain accounts for a single bar. Renders nothing when the slot has neither
 * such a module nor legacy HTML modules. While present it publishes
 * `--header-message-bar-block-size`, the message-bar term of the header height chain in
 * `app/leap/theme/global-css.js`, with the footprint of the bar it renders.
 *
 * @component
 * @returns {JSX.Element|null} The message bar, or `null` when the slot is empty.
 */
const MessageBar = () => {
    const recipe = useRecipe({recipe: messageBarRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {data: messageBar} = useCmsContent('message-bar', TYPE_CONTENT_SLOT)
    const body = messageBar?.c_body
    const leapBar = useMemo(() => findMessageBar(body), [body])
    const {hasCmsModules} = useMemo(
        () => prepareHtmlForInjection(typeof body === 'string' ? body : undefined),
        [body]
    )

    if (!leapBar && !hasCmsModules) {
        return null
    }

    const leapBlockSize = leapBar?.hasCta
        ? 'var(--leap-message-bar-cta-block-size)'
        : 'var(--leap-message-bar-block-size)'
    const blockSize = leapBar ? leapBlockSize : 'var(--legacy-message-bar-block-size)'

    return (
        <Box css={styles}>
            <Global
                styles={{
                    ':root': {
                        '--header-message-bar-block-size': blockSize
                    }
                }}
            />
            {/* light like the header */}
            <Chakra3Theme theme="light">
                {leapBar ? (
                    <CmsModules assetId="message-bar" modules={leapBar.modules} />
                ) : (
                    <CMSContent
                        id="message-bar"
                        type={TYPE_CONTENT_SLOT}
                        content={messageBar}
                        showError={false}
                    />
                )}
            </Chakra3Theme>
        </Box>
    )
}

export default MessageBar
